import { Injectable } from '@angular/core';
import { Software } from '../../models/software.model';
import { Project } from '../../models/project.model';
import { Observable, Subject, of, BehaviorSubject } from 'rxjs';
import { map, filter, scan, catchError, tap } from 'rxjs/operators';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import { MessageService } from '../message.service';
import { LOAD, ADD, EDIT, REMOVE, ProjectStore } from '../stores/project.store';

let headers  = new HttpHeaders({'Content-Type':'application/json; charset=utf-8'});
headers = headers.set('Accept', 'application/json');

const HTTPOPTIONS = {
  headers: headers
};

const STORAGE_KEY = 'PROJECTS';

export class LocalSoftware implements Software{

  static create(data: any) {
    return new LocalSoftware(data.uuid, data.name);
  }

  constructor( uuid?: string,
               name?: string) {
  }
}

export class LocalProject implements Project {

  static create(data: any) {
    return new LocalProject(data.uuid, data.name, data.description, data.beginn, data.end, data.company_name, data.software);
  }

  constructor(public uuid?: string,
              public name?: string,
              public description?: string,
              public beginn?: string,
              public end?: string,
              public company_name?: string,
              public software?: Software[]) {
  }
}

@Injectable()
export class ProjectService {
  itemsPerPage = 20;

  projects: Project[] = [];
  projects$: Observable<Project[]>;
  project: Project = {};

  private domainUrl = 'https://apimain.gerd-gruber.de/';  // URL to web api
  private projectsUrl = this.domainUrl + 'projects';  // URL to web api
  private projectUrl = this.domainUrl + 'project';  // URL to web api

  constructor(private http: HttpClient,
              private messageService: MessageService,
              private projectStore: ProjectStore) {
    this.projects$ = projectStore.items$;
  }

  updateProject(project: Project) {
    const url = `${this.projectUrl}/${project.uuid}`;

    return this.http.put<Project>(url, project, HTTPOPTIONS)
      .pipe(
        tap(editProject => {
          const actionType = EDIT;
          const action = {type: actionType, data: editProject};
          this.projectStore.dispatch(action);
          this.log(`update project with uuid=${editProject.uuid}`)
        }),
        catchError(this.handleError)
      );
  }

  createProject(project: Project) {
    const url = `${this.projectUrl}`;

    return this.http.post<Project>(url, project, HTTPOPTIONS)
      .pipe(
        tap(savedProject => {
          const actionType = ADD;
          const action = {type: actionType, data: savedProject};
          this.projectStore.dispatch(action);
          this.log(`create new project with uuid=${savedProject.uuid}`)
        }),
        catchError(this.handleError)
      );
  }

  saveProject(project: Project) {
    let saveProjectObservable = project.uuid ? this.updateProject(project) : this.createProject(project);

    return saveProjectObservable
      .pipe(
        tap(savedProject => {

        }),
        catchError(this.handleError)
      );
  };

  deleteProject(project: Project) {
    const url = `${this.projectUrl}/${project.uuid}`;

    return this.http.delete<Project>(url, HTTPOPTIONS)
      .pipe(
        tap(deletedProject => {
          const actionType = REMOVE;
          const action = {type: actionType, data: deletedProject};
          this.projectStore.dispatch(action);
          this.log(`deleted project with uuid=${deletedProject.uuid}`)
        }),
        catchError(this.handleError)
      );
  }

  saveToLocalStorage() {
    this.projects$
      .subscribe((projects: Project[]) => {
          this.projects.push(...projects);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(this.projects));
      });
  }

  loadAllProjects(): Observable<Project[]> {
    return this.http.get<Project[]>(this.projectsUrl, HTTPOPTIONS)
      .pipe(
        tap(allProjects => {
          const actionType = LOAD;
          const action = {type: actionType, data: allProjects};
          this.projectStore.dispatch(action);
          this.log('fetched projects')
          this.saveToLocalStorage();
        }),
        catchError(this.handleError)
      );

  }

  loadFromLocalStorage() {
    const projectsString = localStorage.getItem(STORAGE_KEY);
    if (projectsString) {
      this.projects = <Project[]>JSON.parse(projectsString);
    } else {
      this.projects = [];
    }
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
    }

    return of(error.error);
  }

  private log(message: string) {
    this.messageService.add(`ProjectService: ${message}`);
  }
}
